import React, {useState} from "react";
import {Col, Collapse} from "reactstrap";
import IconButton from "../../../base/button/icon_button";
import {CircularProgress, Fade, SvgIcon} from "@mui/material";
import {ReactComponent as ToggleIcon} from "../../../../../assets/images/warranty-statuses-reasons/toggle.svg";
import classnames from "classnames";
import useIsMounted from "../../../../hooks/use-is-mounted";
import LazyImage from "../../lazy-image";
import {Visibility, VisibilityOff} from "@mui/icons-material";
import {ReactComponent as RemoveIcon} from "../../../../../assets/images/remove.svg";
import {ReactComponent as TimeIcon} from "../../../../../assets/images/notifications/time.svg";
import Button from "../../../base/button/button";
import DateUtils from "../../../../../core/services/date-utils";

const ProductFeedbackCard = ({
                                 feedback,
                                 toggleVisibility,
                                 remove,
                                 parent = null,
                             }) => {
    const [togglingVisibility, setTogglingVisibility] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [childrenVisible, setChildrenVisible] = useState(5);
    const [open, setOpen] = useState(false);
    const isMounted = useIsMounted();

    /**
     * Toggles the visibility of the current feedback.
     * @return {Promise<void>}
     */
    const toggleFeedbackVisibility = async () => {
        setTogglingVisibility(true);
        await toggleVisibility(feedback);
        if (!isMounted()) return;
        setTogglingVisibility(false);
    }

    /**
     * Removes the current feedback.
     * @return {Promise<void>}
     */
    const removeFeedback = async () => {
        setRemoving(true);
        await remove(feedback);
        if (!isMounted()) return;
        setRemoving(false);
    }

    return (
        <>
            <div className={classnames('product-feedback-card', {'child': !!parent})}>
                <div className={'d-flex justify-content-between'}>
                    <div className={'d-flex'}>
                        <div className={'image'}>
                            <LazyImage
                                src={feedback?.user?.profileImageUrl ?? ''}
                                alt={feedback?.user?.firstName ?? feedback?.user?.lastName}
                            />
                        </div>
                        <div className={'d-flex flex-column ml-2'}>
                            <p className={'customer'}>
                                {feedback?.user?.customer?.name}
                            </p>
                            <p className={'user'}>
                                {feedback?.user?.firstName ?? ""} {feedback?.user?.lastName ?? ''}
                            </p>
                        </div>
                    </div>
                    <div className={'d-flex justify-content-end align-items-center'}>
                        <IconButton
                            disabled={togglingVisibility}
                            onPressed={toggleFeedbackVisibility}
                            color={'paper.secondary'}
                            className={'mr-2'}
                        >
                            {
                                togglingVisibility
                                    ? <CircularProgress size={15}/>
                                    // isAvailable is used for feedbacks in lookup product info
                                    : feedback?.isVisible || feedback?.isAvailable
                                        ? <Visibility/>
                                        : <VisibilityOff/>
                            }
                        </IconButton>
                        <IconButton
                            onPressed={removeFeedback}
                            disabled={removing}
                            color={'paper.error'}
                        >
                            {
                                removing
                                    ? <CircularProgress size={15}/>
                                    : <SvgIcon component={RemoveIcon} className='icon normal'/>
                            }
                        </IconButton>
                    </div>
                </div>
                <div className={'content'}>
                    {feedback?.msg ?? ''}
                </div>
                <div className={'d-flex align-items-center justify-content-between'}>
                    <p className={'date'}>
                        <TimeIcon className={'mx-1'}/>
                        {
                            feedback?.submitDateTime
                                ? DateUtils.serverToLocal(feedback.submitDateTime).format(DateUtils.dateTimeFormat)
                                : ''
                        }
                    </p>
                    <Fade in={!!feedback?.children?.length}>
                        <div className={'d-flex justify-content-end'}>
                            <IconButton
                                disabled={removing}
                                className={'ml-1 toggle-button'}
                                onPressed={() => setOpen(prevState => !prevState)}>
                                <SvgIcon
                                    component={ToggleIcon}
                                    className={classnames(
                                        'icon normal toggle-icon',
                                        {'open': open}
                                    )}
                                />
                            </IconButton>
                        </div>
                    </Fade>
                </div>
                <div className={classnames('w-100 transition', {'mb-3': open})}>
                    <Collapse isOpen={open}>
                        <div className={'d-flex flex-column'}>
                            <Col xs={12} className={'d-flex align-items-center p-0 mb-3'}>
                                <div className={'dotted-divider'}/>
                            </Col>
                            {
                                feedback?.children
                                    ?.slice(0, Math.min(childrenVisible, feedback?.children?.length ?? 0))
                                    ?.map(child => (
                                        <Col key={child.id} xs={12}>
                                            <ProductFeedbackCard
                                                feedback={child}
                                                parent={feedback}
                                                toggleVisibility={(child) => toggleVisibility(child, feedback)}
                                                remove={(child) => remove(child, feedback)}
                                            />
                                        </Col>
                                    ))
                            }
                            {
                                (childrenVisible < (feedback?.children?.length ?? 0)) &&
                                <div className={'col-12 d-flex w-100 justify-content-end'}>
                                    <Button
                                        onClick={() => setChildrenVisible(prevState => prevState + 5)}
                                        variant={'text'}
                                        color={'primary'}
                                    >
                                        Show more
                                    </Button>
                                </div>
                            }
                        </div>
                    </Collapse>
                </div>
            </div>
        </>
    )
}

export default ProductFeedbackCard;
